import React, { useEffect, useMemo, useState } from "react";
import { useTagFamilies, TagList } from "../components/DossierTags";
import { useNavigate } from "react-router-dom";
import { Search, Upload, RefreshCcw, Download, Filter, ArrowUpRight, LayoutGrid, List as ListIcon, Trash2, X, MapPin, Map, Phone, Mail, Landmark } from "lucide-react";
import AppLayout, { PageHeader } from "../components/AppLayout";
import AiInsight from "../components/AiInsight";
import StatusBadge, { STATUS_META } from "../components/StatusBadge";
import { CrmStatusBadge, useCrmStatuses } from "../components/CrmStatus";

const EQUIPE_TYPES = [["regie", "Régie"], ["sous_traitant", "Sous-traitant"], ["financier", "Partenaire financier"], ["installateur", "Installateur"]];
const EQUIPE_VAL = { regie: (d) => d.regie || d.partenaire, sous_traitant: (d) => d.sous_traitant, financier: (d) => d.partenaire_financier, installateur: (d) => d.installateur };

function EquipeCell({ d }) {
  const rows = EQUIPE_TYPES.map(([k, l]) => [l, EQUIPE_VAL[k](d)]).filter(([, v]) => v);
  if (!rows.length) return "—";
  return <div className="space-y-0.5" data-testid={`equipe-cell-${d.id}`}>{rows.map(([l, v]) => <div key={l} className="whitespace-nowrap"><span className="text-norea-textMuted">{l} :</span> {v}</div>)}</div>;
}

function CrmFilter({ value, onChange }) {
  const data = useCrmStatuses();
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="input-field max-w-[200px]" data-testid="filter-crm-status">
      <option value="">Tous les statuts dossier</option>
      {data?.sans_statut > 0 && <option value="none">Sans statut ({data.sans_statut})</option>}
      {(data?.items || []).map((x) => <option key={x.id} value={x.id}>{x.label} ({x.count})</option>)}
    </select>
  );
}
import KanbanView from "../components/KanbanView";
import { api } from "../lib/api";
import { useLive } from "../lib/live";
import { toast } from "sonner";

const STATUS_OPTIONS = ["", "PROCESSING", "INCOMPLET", "A_VERIFIER", "COMPLET", "IMPORTE", "TRAITE", "ERREUR"];
const PRODUIT_OPTIONS = ["", "PV", "PAC", "ITE"];
const PAGE_SIZE = 100;

export default function DossiersPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", produit: "", search: "", tag: "", crm_status: "", equipe_type: "", equipe: "" });
  const tagFamilies = useTagFamilies();
  const [showUpload, setShowUpload] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("norea_view_mode") || "table");
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const navigate = useNavigate();

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allVisibleSelected = items.length > 0 && items.every((d) => selected.has(d.id));
  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map((d) => d.id)));
    }
  };

  const clearSelection = () => setSelected(new Set());

  const handleBulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/dossiers/bulk-delete", { ids });
      toast.success(`${data.deleted} dossier(s) supprimé(s)`);
      setSelected(new Set());
      setConfirmDelete(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setBulkDeleting(false);
    }
  };

  const setViewModeAndStore = (m) => {
    setViewMode(m);
    localStorage.setItem("norea_view_mode", m);
  };

  const buildParams = () => {
    const params = { limit: PAGE_SIZE };
    if (filters.status) params.status = filters.status;
    if (filters.produit) params.produit = filters.produit;
    if (filters.search) params.search = filters.search;
    if (filters.tag) params.tag = filters.tag;
    if (filters.crm_status) params.crm_status = filters.crm_status;
    if (filters.equipe_type) params.equipe_type = filters.equipe_type;
    if (filters.equipe) params.equipe = filters.equipe;
    return params;
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dossiers", { params: buildParams() });
      setItems(data.items || []);
      setTotal(data.total ?? (data.items || []).length);
      setStatusCounts(data.status_counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Chargement des dossiers impossible");
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get("/dossiers", { params: { ...buildParams(), skip: items.length } });
      setItems((prev) => [...prev, ...(data.items || []).filter((d) => !prev.some((p) => p.id === d.id))]);
      setTotal(data.total ?? total);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [filters.status, filters.produit, filters.search, filters.tag]);

  // Live refresh: reload when a dossier starts or completes
  const { subscribe } = useLive();
  useEffect(() => {
    return subscribe((event) => {
      if (
        event.type === "dossier:started" ||
        event.type === "dossier:completed" ||
        event.type === "dossier:status" ||
        event.type === "dossier:deleted" ||
        event.type === "dossiers:bulk-deleted"
      ) {
        load();
      }
    });
  }, [subscribe]);

  const exportCsv = async () => {
    let all = items;
    if (items.length < total) {
      toast.message("Préparation de l'export complet…");
      all = [];
      while (all.length < total) {
        const { data } = await api.get("/dossiers", { params: { ...buildParams(), limit: 500, skip: all.length } });
        if (!data.items?.length) break;
        all = all.concat(data.items);
      }
    }
    const headers = ["Date", "Client", "CP", "Ville", "Produit", "Organisme", "Statut", "Équipe", "Durée (s)"];
    const rows = all.map((d) => [
      d.created_at?.slice(0, 16).replace("T", " "),
      `${d.extraction?.nom || ""} ${d.extraction?.prenom || ""}`.trim(),
      d.extraction?.code_postal || "",
      d.extraction?.ville || "",
      d.produit || "",
      d.extraction?.organisme_financier || "",
      d.status || "",
      d.partenaire || "",
      Math.round((d.duration_ms || 0) / 1000),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dossiers_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AppLayout>
      <PageHeader
        title="Dossiers"
        subtitle={`${items.length} / ${total} dossier(s) — Liste filtrable et exportable`}
        breadcrumb={[{ label: "Norea", to: "/" }, "Dossiers"]}
        actions={
          <>
            <div className="inline-flex items-center gap-0.5 bg-white border border-norea-border rounded-md p-0.5" data-testid="view-toggle">
              <button
                onClick={() => setViewModeAndStore("table")}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded transition ${
                  viewMode === "table" ? "bg-norea-primary text-white" : "text-norea-textMuted hover:text-norea-text"
                }`}
                data-testid="view-toggle-table"
              >
                <ListIcon className="w-3.5 h-3.5" />
                Tableau
              </button>
              <button
                onClick={() => setViewModeAndStore("kanban")}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded transition ${
                  viewMode === "kanban" ? "bg-norea-primary text-white" : "text-norea-textMuted hover:text-norea-text"
                }`}
                data-testid="view-toggle-kanban"
              >
                <LayoutGrid className="w-3.5 h-3.5" />
                Kanban
              </button>
            </div>
            <button onClick={exportCsv} className="btn-secondary" data-testid="btn-export-csv">
              <Download className="w-4 h-4" />
              Exporter CSV
            </button>
            <button
              onClick={() => setShowUpload(true)}
              className="btn-primary"
              data-testid="btn-upload-dossier"
            >
              <Upload className="w-4 h-4" />
              Traiter un dossier
            </button>
          </>
        }
      />
      <AiInsight scope="dossiers" title="Synthèse IA du portefeuille" className="mb-5" />

      {/* Filters */}
      <div className="card-flat p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="dossiers-filters">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-norea-textDisabled" />
          <input
            type="text"
            placeholder="Rechercher nom, ville, CP, partenaire…"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="input-field pl-10"
            data-testid="filter-search"
          />
        </div>
        <CrmFilter value={filters.crm_status} onChange={(v) => setFilters({ ...filters, crm_status: v })} />
        <select value={filters.equipe_type} onChange={(e) => setFilters({ ...filters, equipe_type: e.target.value })} className="input-field max-w-[170px]" data-testid="filter-equipe-type">
          <option value="">Équipe : tous types</option>{EQUIPE_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input value={filters.equipe} onChange={(e) => setFilters({ ...filters, equipe: e.target.value })} placeholder="Nom exact de l'équipe" className="input-field max-w-[170px]" data-testid="filter-equipe" />
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="input-field max-w-[160px]"
          data-testid="filter-status"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s ? STATUS_META[s]?.label || s : "Tous statuts"}
            </option>
          ))}
        </select>
        <select
          value={filters.produit}
          onChange={(e) => setFilters({ ...filters, produit: e.target.value })}
          className="input-field max-w-[140px]"
          data-testid="filter-produit"
        >
          {PRODUIT_OPTIONS.map((p) => (
            <option key={p} value={p}>
              {p || "Tous produits"}
            </option>
          ))}
        </select>
        <select
          value={filters.tag}
          onChange={(e) => setFilters({ ...filters, tag: e.target.value })}
          className="input-field max-w-[180px]"
          data-testid="filter-tag"
        >
          <option value="">Tous tags</option>
          {tagFamilies.map((f) => (
            <optgroup key={f.id} label={f.name}>
              {f.tags.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </optgroup>
          ))}
        </select>
        <button onClick={load} className="btn-secondary" data-testid="btn-refresh-list">
          <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Actualiser
        </button>
      </div>

      {/* Bulk-action bar (visible when at least one row is selected) */}
      {selected.size > 0 && (
        <div
          className="mb-4 flex items-center justify-between gap-3 px-4 py-2.5 bg-norea-primary text-white rounded-md shadow-sm"
          data-testid="bulk-action-bar"
        >
          <div className="flex items-center gap-3 text-sm">
            <span className="font-semibold tabular-nums" data-testid="bulk-selected-count">
              {selected.size} dossier(s) sélectionné(s)
            </span>
            <button
              onClick={clearSelection}
              className="text-white/80 hover:text-white text-xs underline underline-offset-2"
              data-testid="btn-bulk-clear"
            >
              Désélectionner
            </button>
          </div>
          <button
            onClick={() => setConfirmDelete(true)}
            disabled={bulkDeleting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white text-status-erreurText hover:bg-status-erreurBg border border-white rounded transition disabled:opacity-50"
            data-testid="btn-bulk-delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Supprimer la sélection
          </button>
        </div>
      )}

      {/* Table or Kanban */}
      {viewMode === "kanban" ? (
        <KanbanView
          items={items}
          statusCounts={statusCounts}
          onChange={({ id, status }) => {
            setItems((prev) => prev.map((d) => (d.id === id ? { ...d, status } : d)));
          }}
        />
      ) : (
      <div className="card-flat overflow-hidden" data-testid="dossiers-table">

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-norea-bg border-b border-norea-border sticky top-0">
              <tr className="text-left">
                <th className="px-3 py-3 w-[36px]">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 accent-norea-primary cursor-pointer"
                    data-testid="bulk-select-all"
                    aria-label="Tout sélectionner"
                  />
                </th>
                <Th>Date</Th>
                <Th>Client</Th>
                <Th>Localisation</Th>
                <Th>Contact</Th>
                <Th>Produit</Th>
                <Th>Organisme</Th>
                <Th>Statut</Th>
                <Th>Équipe</Th>
                <Th>Liens</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={11} className="text-center text-sm text-norea-textMuted py-12">
                    Chargement…
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={11} className="text-center text-sm text-norea-textMuted py-12">
                    Aucun dossier trouvé.
                  </td>
                </tr>
              )}
              {items.map((d) => (
                <tr
                  key={d.id}
                  className={`border-b border-norea-border transition-colors cursor-pointer ${
                    selected.has(d.id) ? "bg-norea-accent/10 hover:bg-norea-accent/15" : "hover:bg-norea-surfaceHover"
                  }`}
                  onClick={() => navigate(`/dossiers/${d.id}`)}
                  data-testid={`dossier-row-${d.id}`}
                >
                  <td
                    className="px-3 py-3 align-middle"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(d.id)}
                      onChange={() => toggleSelect(d.id)}
                      className="w-4 h-4 accent-norea-primary cursor-pointer"
                      data-testid={`dossier-select-${d.id}`}
                      aria-label="Sélectionner ce dossier"
                    />
                  </td>
                  <Td className="text-norea-textMuted text-xs tabular-nums">{d.created_at?.slice(0, 16).replace("T", " ")}</Td>
                  <Td className="font-medium">
                    {d.extraction?.nom || "—"} {d.extraction?.prenom || ""}
                    <TagList ids={d.tags} />
                  </Td>
                  <Td className="text-xs text-norea-textMuted max-w-[220px]">
                    {d.extraction?.adresse && (
                      <div className="truncate text-norea-text" title={d.extraction.adresse}>{d.extraction.adresse}</div>
                    )}
                    <div className="tabular-nums">
                      {d.extraction?.code_postal || ""} {d.extraction?.ville || ""}
                    </div>
                  </Td>
                  <Td className="text-xs">
                    <ContactCell d={d} />
                  </Td>
                  <Td>
                    <span className="inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold bg-norea-surfaceHover text-norea-text border border-norea-border rounded">
                      {d.produit || "?"}
                    </span>
                  </Td>
                  <Td className="text-xs">{d.extraction?.organisme_financier || "—"}</Td>
                  <Td>
                    <CrmStatusBadge id={d.crm_status} testid={`crm-status-${d.id}`} />
                    <div className="mt-1"><StatusBadge status={d.status} /></div>
                  </Td>
                  <Td className="text-xs"><EquipeCell d={d} /></Td>
                  <Td onClick={(e) => e.stopPropagation()}>
                    <ExternalLinks extraction={d.extraction} />
                  </Td>
                  <Td>
                    <ArrowUpRight className="w-4 h-4 text-norea-textDisabled" />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {!loading && items.length < total && (
        <div className="flex justify-center mt-4">
          <button onClick={loadMore} disabled={loadingMore} className="btn-secondary" data-testid="dossiers-load-more">
            {loadingMore ? "Chargement…" : `Charger plus (${total - items.length} restants)`}
          </button>
        </div>
      )}

      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); load(); }} />}
      {confirmDelete && (
        <ConfirmDeleteModal
          count={selected.size}
          busy={bulkDeleting}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={handleBulkDelete}
        />
      )}
    </AppLayout>
  );
}

const Th = ({ children }) => (
  <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-norea-textMuted">{children}</th>
);
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>;

function UploadModal({ onClose, onDone }) {
  const [files, setFiles] = useState([]);
  const [sender, setSender] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!files.length) {
      setError("Sélectionnez au moins un PDF");
      return;
    }
    setUploading(true);
    setError("");
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    if (sender) fd.append("sender_email", sender);
    try {
      const { data } = await api.post("/dossiers/manual-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Navigate immediately to the detail page to watch the workflow live
      onClose();
      if (data?.dossier_id) {
        navigate(`/dossiers/${data.dossier_id}`);
      } else {
        onDone();
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Échec du traitement");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg border border-norea-border" onClick={(e) => e.stopPropagation()} data-testid="upload-modal">
        <div className="px-6 py-4 border-b border-norea-border">
          <h3 className="font-heading text-xl font-medium text-norea-text">Traiter un dossier manuellement</h3>
          <p className="text-xs text-norea-textMuted mt-1">
            Uploadez les PDF du dossier. L'agent les analysera automatiquement.
          </p>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div>
            <label className="overline block mb-1.5">Pièces du dossier (PDF)</label>
            <input
              type="file"
              multiple
              accept="application/pdf"
              onChange={(e) => setFiles(Array.from(e.target.files))}
              className="input-field"
              data-testid="upload-files-input"
              required
            />
            {files.length > 0 && (
              <div className="mt-2 text-xs text-norea-textMuted">
                {files.length} fichier(s) sélectionné(s)
              </div>
            )}
          </div>
          <div>
            <label className="overline block mb-1.5">Partenaire (optionnel)</label>
            <input
              type="email"
              value={sender}
              onChange={(e) => setSender(e.target.value)}
              placeholder="ex: isolation@monbureau-etude.fr"
              className="input-field"
              data-testid="upload-sender-input"
            />
          </div>
          {error && (
            <div className="text-sm text-status-erreurText bg-status-erreurBg border border-status-erreurBorder px-3 py-2 rounded-md">
              {error}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary" data-testid="btn-upload-cancel">
              Annuler
            </button>
            <button type="submit" disabled={uploading} className="btn-primary" data-testid="btn-upload-submit">
              {uploading ? "Traitement en cours…" : "Lancer le traitement"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ConfirmDeleteModal({ count, busy, onCancel, onConfirm }) {
  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6"
      onClick={busy ? undefined : onCancel}
      data-testid="confirm-delete-modal"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md border border-norea-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-norea-border flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-status-erreurBg flex items-center justify-center text-status-erreurText flex-shrink-0">
            <Trash2 className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-xl font-medium text-norea-text">Supprimer définitivement</h3>
            <p className="text-xs text-norea-textMuted mt-1">Cette action est irréversible.</p>
          </div>
          <button
            onClick={onCancel}
            disabled={busy}
            className="text-norea-textMuted hover:text-norea-text"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm text-norea-text">
            Êtes-vous sûr de vouloir supprimer{" "}
            <strong className="font-semibold">
              {count} dossier{count > 1 ? "s" : ""}
            </strong>{" "}
            ? Les pièces jointes archivées et l'historique du workflow seront aussi supprimés.
          </p>
          <p className="text-xs text-norea-textMuted mt-2 italic">
            Astuce : si les emails sont encore taggés dans Gmail, ces dossiers seront ré-ingérés au prochain polling.
          </p>
        </div>
        <div className="px-6 py-4 bg-norea-bg border-t border-norea-border flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="btn-secondary"
            data-testid="btn-confirm-delete-cancel"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-status-erreurText text-white hover:opacity-90 rounded-md transition disabled:opacity-50"
            data-testid="btn-confirm-delete"
          >
            <Trash2 className="w-4 h-4" />
            {busy ? "Suppression…" : "Supprimer"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ContactCell({ d }) {
  const tel = d.extraction?.telephone || d.extraction?.tel;
  const mail = d.extraction?.email || d.extraction?.email_client;
  if (!tel && !mail) return <span className="text-norea-textMuted">—</span>;
  return (
    <div className="space-y-0.5" onClick={(e) => e.stopPropagation()}>
      {tel && (
        <a
          href={`tel:${tel.replace(/\s+/g, "")}`}
          className="flex items-center gap-1 text-norea-text hover:text-norea-primary transition"
          data-testid="dossier-contact-tel"
          title={`Appeler ${tel}`}
        >
          <Phone className="w-3 h-3 shrink-0 opacity-70" />
          <span className="tabular-nums truncate max-w-[120px]">{tel}</span>
        </a>
      )}
      {mail && (
        <a
          href={`mailto:${mail}`}
          className="flex items-center gap-1 text-norea-text hover:text-norea-primary transition"
          data-testid="dossier-contact-mail"
          title={mail}
        >
          <Mail className="w-3 h-3 shrink-0 opacity-70" />
          <span className="truncate max-w-[150px]">{mail}</span>
        </a>
      )}
    </div>
  );
}

function ExternalLinks({ extraction }) {
  const parts = [
    extraction?.adresse,
    extraction?.code_postal,
    extraction?.ville,
  ].filter(Boolean).join(" ");
  const hasLocation = !!(extraction?.adresse || extraction?.ville || extraction?.code_postal);
  if (!hasLocation) return <span className="text-norea-textMuted text-xs">—</span>;
  const query = encodeURIComponent(parts);
  const gmapUrl = `https://www.google.com/maps/search/?api=1&query=${query}`;
  // Géoportail accepts a "q" search parameter on the carte view and centers on the result.
  const geoportailUrl = `https://www.geoportail.gouv.fr/carte?q=${query}&l0=GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2::GEOPORTAIL:OGC:WMTS(1)&l1=CADASTRALPARCELS.PARCELLAIRE_EXPRESS::GEOPORTAIL:OGC:WMTS(1)`;
  // Atlas du Patrimoine — Ministère de la Culture. Accepts ap_cp / ap_ville / ap_adr.
  // ap_ter=FXX restricts to mainland France + Corsica. Department code is derived from CP[0..2].
  const cp = extraction?.code_postal || "";
  const dep = cp.length >= 2 ? cp.slice(0, 2) : "";
  const atlasParams = new URLSearchParams({ ap_ter: "FXX" });
  if (dep) atlasParams.set("ap_adm", `dep_${dep}`);
  if (cp) atlasParams.set("ap_cp", cp);
  if (extraction?.ville) atlasParams.set("ap_ville", extraction.ville);
  if (extraction?.adresse) atlasParams.set("ap_adr", extraction.adresse);
  const atlasUrl = `http://atlas.patrimoines.culture.fr/atlas/trunk/index.php?${atlasParams.toString()}`;
  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <a
        href={gmapUrl}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center justify-center w-7 h-7 rounded border border-norea-border bg-white hover:bg-norea-bg hover:border-norea-primary/40 hover:text-norea-primary transition"
        title="Ouvrir dans Google Maps"
        data-testid="link-google-maps"
      >
        <MapPin className="w-3.5 h-3.5" />
      </a>
      <a
        href={geoportailUrl}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center justify-center w-7 h-7 rounded border border-norea-border bg-white hover:bg-norea-bg hover:border-norea-primary/40 hover:text-norea-primary transition"
        title="Voir le cadastre (Géoportail)"
        data-testid="link-geoportail"
      >
        <Map className="w-3.5 h-3.5" />
      </a>
      <a
        href={atlasUrl}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center justify-center w-7 h-7 rounded border border-norea-border bg-white hover:bg-norea-bg hover:border-norea-primary/40 hover:text-norea-primary transition"
        title="Atlas du Patrimoine (Monuments historiques, sites protégés)"
        data-testid="link-atlas-patrimoine"
      >
        <Landmark className="w-3.5 h-3.5" />
      </a>
    </div>
  );
}

